import {LayerWrapper} from '@volkswagen-onehub/components-core';

import type {Subscription} from '@volkswagen-onehub/feature-hub-utils';
import type {
  DisclaimerLayerDescriptionV2,
  LayerDescriptionV2,
  LayerEventPayload,
  LayerManagerV2,
  LayerManagerV24,
} from '@volkswagen-onehub/layer-manager';
import * as React from 'react';

import {
  AlertLayer,
  ContentLayerV2,
  CustomLayer,
  DisclaimerLayer,
  FocusLayer,
  FullscreenLayer,
  InteractionLayer,
} from './layers';
import {Shim} from './shim';

export interface LayerManagerComponentProps {
  readonly layerManager: LayerManagerV2 | LayerManagerV24;
}

export interface LayerManagerComponentState {
  readonly nonDisclaimerLayers: LayerDescriptionV2[];
  readonly disclaimerLayer: DisclaimerLayerDescriptionV2 | undefined;
}

function getDisclaimerLayer(
  layerManager: LayerManagerV2 | LayerManagerV24,
): DisclaimerLayerDescriptionV2 | undefined {
  return 'getDisclaimerLayer' in layerManager
    ? layerManager.getDisclaimerLayer()
    : undefined;
}

export class LayerManagerComponent extends React.Component<
  LayerManagerComponentProps,
  LayerManagerComponentState
> {
  public static getDerivedStateFromProps(
    props: LayerManagerComponentProps,
  ): LayerManagerComponentState {
    return {
      nonDisclaimerLayers: props.layerManager.getLayers(),
      disclaimerLayer: getDisclaimerLayer(props.layerManager),
    };
  }

  public readonly state: LayerManagerComponentState = {
    nonDisclaimerLayers: this.props.layerManager.getLayers(),
    disclaimerLayer: getDisclaimerLayer(this.props.layerManager),
  };

  private listener?: Subscription;

  public shouldComponentUpdate(
    _nextProps: LayerManagerComponentProps,
    nextState: LayerManagerComponentState,
  ): boolean {
    return (
      this.state.nonDisclaimerLayers !== nextState.nonDisclaimerLayers ||
      this.state.disclaimerLayer !== nextState.disclaimerLayer
    );
  }

  public componentDidMount(): void {
    this.listener = this.props.layerManager.subscribe(this.update);
  }

  public componentWillUnmount(): void {
    if (this.listener) {
      this.listener.unsubscribe();
    }
  }

  public renderLayer(
    layerDescription: LayerDescriptionV2 | DisclaimerLayerDescriptionV2,
    active: boolean,
  ): JSX.Element {
    const {close, id, options} = layerDescription.layer;

    switch (layerDescription.type) {
      case 'ALERT':
        return (
          <AlertLayer
            disableReturnFocus={options.disableReturnFocus}
            onDeactivation={options.onFocusTrapDeactivated}
            layer={layerDescription.layer}
            active={active}
            key={id}
            onEscKeyUp={options.userCloseable ? close : undefined}
            scrollElementRef={(el: HTMLDivElement) => {
              this.createRef(id, el);
            }}
          />
        );
      case 'CONTENT':
        return <span>was removed</span>;
      case 'CONTENTV2':
        return (
          <ContentLayerV2
            disableReturnFocus={options.disableReturnFocus}
            onDeactivation={options.onFocusTrapDeactivated}
            layer={layerDescription.layer}
            active={active}
            key={id}
            onEscKeyUp={options.userCloseable ? close : undefined}
            scrollElementRef={(el: HTMLDivElement) => {
              this.createRef(id, el);
            }}
          />
        );
      case 'FOCUS':
        return (
          <FocusLayer
            disableReturnFocus={options.disableReturnFocus}
            onDeactivation={options.onFocusTrapDeactivated}
            layer={layerDescription.layer}
            active={active}
            key={id}
            onEscKeyUp={options.userCloseable ? close : undefined}
            scrollElementRef={(el: HTMLDivElement) => {
              this.createRef(id, el);
            }}
          />
        );
      case 'INTERACTION':
        return (
          <InteractionLayer
            disableReturnFocus={options.disableReturnFocus}
            onDeactivation={options.onFocusTrapDeactivated}
            layer={layerDescription.layer}
            active={active}
            key={id}
            onEscKeyUp={options.userCloseable ? close : undefined}
            scrollElementRef={(el: HTMLDivElement) => {
              this.createRef(id, el);
            }}
          />
        );
      case 'DISCLAIMER':
        return (
          <DisclaimerLayer
            disableReturnFocus={options.disableReturnFocus}
            onDeactivation={options.onFocusTrapDeactivated}
            layer={layerDescription.layer}
            key={id}
            onEscKeyUp={options.userCloseable ? close : undefined}
            scrollElementRef={(el: HTMLDivElement) => {
              this.createRef(id, el);
            }}
          />
        );

      case 'FULLSCREEN':
        return (
          <FullscreenLayer
            layer={layerDescription.layer}
            active={active}
            key={id}
            onEscKeyUp={options.userCloseable ? close : undefined}
            scrollElementRef={(el: HTMLDivElement) => {
              this.createRef(id, el);
            }}
          />
        );

      case 'CUSTOM':
        return (
          <CustomLayer
            disableReturnFocus={options.disableReturnFocus}
            onDeactivation={options.onFocusTrapDeactivated}
            layer={layerDescription.layer}
            active={active}
            key={id}
            onEscKeyUp={options.userCloseable ? close : undefined}
            scrollElementRef={(el: HTMLDivElement) => {
              this.createRef(id, el);
            }}
          />
        );
    }
  }

  public render(): JSX.Element {
    const {nonDisclaimerLayers, disclaimerLayer} = this.state;

    /**
     * FIXME: `contentCentered` is necessary because of a backward-incompatible change in
     * `LayerWrapper`, hiding styles which are necessary for the correct display behind a
     * new styling prop `contentCentered`.
     * See https://github.com/volkswagen-onehub/components-core/commit/3909a4b9ca104716d52ac65ac68e5e3f70f42f79
     */
    return (
      <LayerWrapper contentCentered>
        {nonDisclaimerLayers.map((layerDescription, index) => {
          const {type, layer} = layerDescription;
          const isTopLayer = index === nonDisclaimerLayers.length - 1;
          const isLayerCloseable = layerDescription.layer.options.userCloseable;
          const shimBackgroundType =
            layerDescription.layer.options.shimBackgroundType;
          const closeButton = layerDescription.layer.options.closeButton;

          return [
            isTopLayer && (
              <Shim
                key="shim"
                layerType={type}
                backgroundType={shimBackgroundType}
                onClick={isLayerCloseable ? layer.close : undefined}
              />
            ),
            isTopLayer && closeButton,
            this.renderLayer(layerDescription, isTopLayer),
          ];
        })}
        {disclaimerLayer && this.renderLayer(disclaimerLayer, true)}
      </LayerWrapper>
    );
  }

  private readonly createRef = (id: string, el: HTMLDivElement | null) => {
    this.props.layerManager.onRender(id, el);
  };

  private readonly update = (event?: LayerEventPayload) => {
    if (!event || event === 'update') {
      this.forceUpdate();
    }
  };
}
